import { useState } from 'react';

interface InteractiveCalculatorProps {
  solidId: string;
  name: string;
  volumeFormula: string;
  surfaceAreaFormula: string;
}

export default function InteractiveCalculator({
  solidId,
  name,
  volumeFormula,
  surfaceAreaFormula
}: InteractiveCalculatorProps) {
  const [inputs, setInputs] = useState<{ [key: string]: string }>({});
  const [volume, setVolume] = useState<number | null>(null);
  const [surfaceArea, setSurfaceArea] = useState<number | null>(null);

  const getInputFields = () => {
    switch (solidId) {
      case 'rectangular-prism':
        return [
          { key: 'length', label: 'Length (l)', unit: 'cm' },
          { key: 'width', label: 'Width (w)', unit: 'cm' },
          { key: 'height', label: 'Height (h)', unit: 'cm' }
        ];
      case 'triangular-prism':
        return [
          { key: 'base', label: 'Triangle Base (b)', unit: 'cm' },
          { key: 'triangleHeight', label: 'Triangle Height (h)', unit: 'cm' },
          { key: 'length', label: 'Prism Length (l)', unit: 'cm' }
        ];
      case 'square-pyramid':
      case 'triangular-pyramid':
        return [
          { key: 'baseArea', label: 'Base Area (B)', unit: 'cm²' },
          { key: 'height', label: 'Height (h)', unit: 'cm' }
        ];
      case 'cylinder':
      case 'cone':
        return [
          { key: 'radius', label: 'Radius (r)', unit: 'cm' },
          { key: 'height', label: 'Height (h)', unit: 'cm' }
        ];
      case 'sphere':
        return [
          { key: 'radius', label: 'Radius (r)', unit: 'cm' }
        ];
      default:
        return [];
    }
  };

  const calculate = () => {
    const values = Object.fromEntries(
      Object.entries(inputs).map(([key, value]) => [key, parseFloat(value)])
    );

    let calcVolume = 0;
    let calcSurfaceArea = 0;

    switch (solidId) {
      case 'rectangular-prism':
        calcVolume = values.length * values.width * values.height;
        calcSurfaceArea = 2 * (values.length * values.width + values.length * values.height + values.width * values.height);
        break;
      case 'triangular-prism':
        calcVolume = (0.5 * values.base * values.triangleHeight) * values.length;
        calcSurfaceArea = values.base * values.triangleHeight + (values.base + values.triangleHeight * 2) * values.length;
        break;
      case 'square-pyramid':
      case 'triangular-pyramid':
        calcVolume = (1 / 3) * values.baseArea * values.height;
        calcSurfaceArea = values.baseArea;
        break;
      case 'cylinder':
        calcVolume = Math.PI * values.radius * values.radius * values.height;
        calcSurfaceArea = 2 * Math.PI * values.radius * values.radius + 2 * Math.PI * values.radius * values.height;
        break;
      case 'cone':
        calcVolume = (1 / 3) * Math.PI * values.radius * values.radius * values.height;
        const slantHeight = Math.sqrt(values.radius * values.radius + values.height * values.height);
        calcSurfaceArea = Math.PI * values.radius * values.radius + Math.PI * values.radius * slantHeight;
        break;
      case 'sphere':
        calcVolume = (4 / 3) * Math.PI * Math.pow(values.radius, 3);
        calcSurfaceArea = 4 * Math.PI * values.radius * values.radius;
        break;
    }

    setVolume(calcVolume);
    setSurfaceArea(calcSurfaceArea);
  };

  const handleInputChange = (key: string, value: string) => {
    setInputs({ ...inputs, [key]: value });
    setVolume(null);
    setSurfaceArea(null);
  };

  const fields = getInputFields();
  const allFieldsFilled = fields.every(field => inputs[field.key] && parseFloat(inputs[field.key]) > 0);

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6 border-2 border-indigo-200">
      <h3 className="text-lg font-bold text-indigo-900 mb-4 flex items-center gap-2">
        <i className="ri-calculator-line text-xl"></i>
        Interactive Calculator
      </h3>

      <div className="space-y-4 mb-6">
        {fields.map(field => (
          <div key={field.key}>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              {field.label}
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                value={inputs[field.key] || ''}
                onChange={(e) => handleInputChange(field.key, e.target.value)}
                className="w-full px-4 py-2 pr-12 border-2 border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                placeholder={`Enter ${field.label.toLowerCase()}`}
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-500">
                {field.unit}
              </span>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={calculate}
        disabled={!allFieldsFilled}
        className={`w-full py-3 rounded-lg font-bold text-white text-sm transition-all whitespace-nowrap ${
          allFieldsFilled
            ? 'bg-indigo-600 hover:bg-indigo-700 cursor-pointer'
            : 'bg-slate-300 cursor-not-allowed'
        }`}
      >
        <i className="ri-calculator-line mr-2"></i>
        Calculate Volume &amp; Surface Area
      </button>

      {volume !== null && surfaceArea !== null && (
        <div className="mt-6 space-y-4">
          <div className="bg-white rounded-lg p-4 border-2 border-indigo-300">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-slate-700">Volume:</span>
              <span className="text-sm text-slate-500">{volumeFormula}</span>
            </div>
            <div className="text-2xl font-bold text-indigo-600">
              {volume.toFixed(2)} cm³
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 border-2 border-indigo-300">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-slate-700">Surface Area:</span>
              <span className="text-sm text-slate-500">{surfaceAreaFormula}</span>
            </div>
            <div className="text-2xl font-bold text-indigo-600">
              {surfaceArea.toFixed(2)} cm²
            </div>
          </div>
        </div>
      )}
    </div>
  );
}